import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NewsCard, LoadingSpinner } from '../'
import './News.css';

const News = ({ newsItems = [], isLoading = false }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width < 960) {
        setItemsPerPage(1);
      } else if (width < 1440) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(3);
      }
      setCurrentPage(0);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canGoNext) {
      goToNextPage();
    }
    if (isRightSwipe && canGoPrev) {
      goToPrevPage();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const allNews = newsItems || [];

  const totalPages = Math.ceil(allNews.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayNews = allNews.slice(startIndex, endIndex);
  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const getVisiblePages = () => {
    const maxDots = 7;
    if (totalPages <= maxDots) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages = [];
    const leftOffset = Math.floor(maxDots / 2);
    const rightOffset = Math.floor(maxDots / 2);

    let start = Math.max(0, currentPage - leftOffset);
    let end = Math.min(totalPages - 1, currentPage + rightOffset);

    if (currentPage < leftOffset) {
      end = Math.min(totalPages - 1, maxDots - 1);
    }

    if (currentPage > totalPages - rightOffset - 1) {
      start = Math.max(0, totalPages - maxDots);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <section className="news-section">
      <div className="news-container">
        <div className="news-header">
          <h2 className="news-title">{t('news.title')}</h2>
          <Link to="/news" className="news-all-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('news.allNews')}
          </Link>
        </div>

        {isLoading ? (
          <div className="news-scroll-wrapper">
            <LoadingSpinner size="small" />
          </div>
        ) : allNews.length === 0 ? (
          <div className="news-empty-state">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{t('news.noNewsAtMoment')}</p>
          </div>
        ) : (
          <div className="news-scroll-controls">
            <button
              className={`news-scroll-btn news-scroll-btn-left ${!canGoPrev ? 'disabled' : ''}`}
              onClick={goToPrevPage}
              aria-label="Previous page"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div
              className="news-scroll-wrapper"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="news-grid">
                {displayNews.map((news, index) => (
                  <div key={news.id} className="fade-in-stagger">
                    <NewsCard news={news} index={index} />
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="news-pagination">
                  {getVisiblePages().map((pageIndex) => (
                    <button
                      key={pageIndex}
                      className={`news-pagination-dot ${pageIndex === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(pageIndex)}
                      aria-label={`Go to page ${pageIndex + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <button
              className={`news-scroll-btn news-scroll-btn-right ${!canGoNext ? 'disabled' : ''}`}
              onClick={goToNextPage}
              aria-label="Next page"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default News;